import clsx from 'clsx';
import React, { useMemo, useReducer, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { Column } from 'react-table';
import { toTitleCase } from '../../../../../../_metronic/helpers';
import { api } from '../../../../../modules/api/api';
import { useAuth } from '../../../../../modules/auth';
import Avatar from '../../../../../modules/components/avatar/Avatar';
import Datatable from '../../../../../modules/components/datatable/Datatable';
import Header from '../../../../../modules/components/datatable/Header';
import UnlabeledBulletColumns from '../../../../../modules/components/list/UnlabeledBulletColumns';
import ListLoading from '../../../../../modules/components/loading/ListLoading';
import { useToast } from '../../../../../modules/components/toast/ToastContext';
import { isoCountryMap } from '../../../../../modules/helpers/isoCountryMap';
import { DataSiftField, SiService, siteIntelligenceServiceLabel } from '../../../mappings';
import EditableCardContainer from '../card/EditableCardContainer';
import { AddWebContent } from '../form/AddWebContent';
import { ServiceSelector, UsePageDataManagerResult } from '../hooks/usePageDataManager';
import { HistoryModal } from '../modal/HistoryModal';
import { getFieldUpdate, SiRequestUser } from '../utils';

interface Sort {
  name: string;
  sort: 1 | -1;
}

interface Props {
  data: any;
  refetch: () => void;
}

export default function AuditRemarksDetail({
  data,
  refetch,
}: Props) {
  const { currentUser, auth, refresh } = useAuth();
  const intl = useIntl();
  const { setToast } = useToast();
  const t = (id: string) => intl.formatMessage({ id: `SI_AUDIT_REMARKS.${id}` });
  const emDash = '—';

  const results = data;
  const auditRemarks = results?.result?.remarks ?? {};

  const auditRemarkItems = useMemo(
    () => [
      { key: 'age_validation', label: t('AGE_VALIDATION') },
      { key: 'shopping_cart', label: t('SHOPPING_CART') },
      { key: 'recurring_payment', label: t('RECURRING_PAYMENT') },
      { key: 'top_up', label: t('TOP_UP') },
      { key: 'contact_us', label: t('CONTACT_US') },
      { key: 'returns_refund_information', label: t('REFUND_EXCHANGE_POLICY') },
      { key: 'refund_provided', label: t('REFUND_POLICY') },
      { key: 'about_us', label: t('ABOUT_US') },
      { key: 'terms_and_conditions', label: t('TERMS_AND_CONDITIONS') },
      { key: 'privacy_policy', label: t('PRIVACY_POLICY') },
    ].map((item) => ({
      ...item,
      isActive: Boolean(auditRemarks[item.key]),
    })),
    [auditRemarks, t]
  );

  const activeAuditRemarkItems = useMemo(
    () => auditRemarkItems.filter((item) => item.isActive),
    [auditRemarkItems]
  );

  return (
    <>
      <EditableCardContainer
        title={t('TITLE')}
      >
        <div className='p-9 vstack gap-9'>
          <div>
            {activeAuditRemarkItems.length > 0 ? (
              <div className='row g-4'>
                {activeAuditRemarkItems.map((item) => (
                  <div key={item.key} className='col-6 col-md-6 col-xl-3'>
                    <div
                      className={clsx(
                        'd-flex align-items-center gap-3 rounded-4 px-3 py-2 border fs-8',
                        {
                          'border-success bg-success bg-opacity-10 text-gray-900': item.isActive,
                          'border-gray-300 bg-light text-gray-400': !item.isActive,
                        }
                      )}
                    >
                      {item.isActive ? (
                        <i className='bi bi-check-circle-fill text-success fs-8 flex-shrink-0' />
                      ) : (
                        <i className='bi bi-circle-fill text-gray-300 fs-8 flex-shrink-0' />
                      )}
                      <span>{item.label}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className='text-gray-600 fs-6'>{emDash}</div>
            )}
          </div>
        </div>
        <div className='d-flex justify-content-end mb-5'>
          <a href='#summary' className='px-8 d-none print-display-button'>
            <i className="bi bi-caret-up-square-fill fs-3"></i>
          </a>
        </div>
      </EditableCardContainer>
    </>
  );
}
